// ============================================================
// 游戏主控 - 3D迷宫游戏
// ============================================================
import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Player } from '../entities/Player';
import { Gem } from '../entities/Gem';
import { Exit } from '../entities/Exit';
import { Hud } from '../systems/Hud';
import {
  generateMaze,
  createMazeMesh,
  buildWallColliders,
  type MazeData,
  type WallAABB,
} from '../systems/MazeGenerator';
import {
  CELL_SIZE,
  PLAYER_HEIGHT,
  GEM_COLLECT_RADIUS,
  EXIT_TRIGGER_RADIUS,
  SKY_COLOR,
  FOG_NEAR_RATIO,
  FOG_FAR_RATIO,
  AMBIENT_LIGHT_INTENSITY,
  HEMI_SKY_COLOR,
  HEMI_GROUND_COLOR,
  HEMI_INTENSITY,
  SUN_COLOR,
  SUN_INTENSITY,
  FLASHLIGHT_INTENSITY,
  FLASHLIGHT_RANGE,
  EXIT_LIGHT_COLOR,
  EXIT_LIGHT_INTENSITY,
  EXIT_LIGHT_RANGE,
  LEVELS,
  type LevelConfig,
} from '../config';

type GameState = 'start' | 'playing' | 'paused' | 'won' | 'lost';

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(75, 1, 0.1, 100);
  private readonly player = new Player();
  private readonly hud = new Hud();

  private mazeGroup: THREE.Group | null = null;
  private mazeData: MazeData | null = null;
  private wallColliders: WallAABB[] = [];
  private gems: Gem[] = [];
  private exit: Exit | null = null;

  private flashlight: THREE.PointLight;
  private ambientLight: THREE.AmbientLight;
  private exitLight: THREE.PointLight;

  private state: GameState = 'start';
  private currentLevel = 0;
  private timeLeft = 0;
  private gemsCollected = 0;
  private totalGems = 0;
  private score = 0;
  private visitedCells = new Set<string>();

  private readonly keys = new Set<string>();
  private readonly loop: Loop;
  private frameCount = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);

    // 光照 - 白天风格
    this.ambientLight = new THREE.AmbientLight('#e8f0e0', AMBIENT_LIGHT_INTENSITY);
    this.scene.add(this.ambientLight);

    const hemiLight = new THREE.HemisphereLight(HEMI_SKY_COLOR, HEMI_GROUND_COLOR, HEMI_INTENSITY);
    this.scene.add(hemiLight);

    // 太阳方向光
    const sun = new THREE.DirectionalLight(SUN_COLOR, SUN_INTENSITY);
    sun.position.set(30, 50, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 120;
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    this.scene.add(sun);

    this.flashlight = new THREE.PointLight('#fff8e8', FLASHLIGHT_INTENSITY, FLASHLIGHT_RANGE);
    this.scene.add(this.flashlight);

    this.exitLight = new THREE.PointLight(EXIT_LIGHT_COLOR, 0, EXIT_LIGHT_RANGE);
    this.scene.add(this.exitLight);

    this.loop = new Loop(
      (delta, elapsed) => this.update(delta, elapsed),
      () => this.render(),
    );

    this.setupInput();
    this.setupHudCallbacks();
    this.hud.showStartScreen(true);
    this.hud.updateLevel(LEVELS[0].name);

    resizeRenderer(this.renderer, this.camera, 2);
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.cleanupMaze();
    this.hud.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
  }

  // ---- 输入 ----
  private setupInput(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'Escape') {
        if (this.state === 'playing') {
          this.state = 'paused';
          document.exitPointerLock();
        } else if (this.state === 'paused') {
          this.state = 'playing';
          this.canvas.requestPointerLock();
        }
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));

    this.canvas.addEventListener('click', () => {
      if (this.state === 'playing' || this.state === 'paused') {
        this.canvas.requestPointerLock();
      }
    });

    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement === this.canvas) {
        if (this.state === 'paused') this.state = 'playing';
      } else {
        if (this.state === 'playing') this.state = 'paused';
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== this.canvas) return;
      if (this.state !== 'playing') return;
      this.player.rotate(e.movementX, e.movementY);
    });
  }

  private setupHudCallbacks(): void {
    this.hud.getStartButton().addEventListener('click', () => {
      this.hud.showStartScreen(false);
      this.startLevel(0);
      this.canvas.requestPointerLock();
    });
  }

  // ---- 关卡管理 ----
  private startLevel(levelIndex: number): void {
    this.cleanupMaze();

    const config = LEVELS[levelIndex];
    this.currentLevel = levelIndex;
    this.gemsCollected = 0;
    this.timeLeft = config.timeLimit;
    this.totalGems = config.gemCount;
    this.visitedCells.clear();
    this.state = 'playing';

    this.hud.updateLevel(config.name);
    this.hud.updateGems(0, this.totalGems);
    this.hud.updateTimer(this.timeLeft);
    this.hud.hideEndScreen();

    // 生成迷宫
    const seed = (levelIndex + 1) * 137 + 42;
    this.mazeData = generateMaze(config.gridSize, seed);
    this.wallColliders = buildWallColliders(this.mazeData);
    this.mazeGroup = createMazeMesh(this.mazeData);
    this.scene.add(this.mazeGroup);

    // 地面
    const mazeSize = config.gridSize * CELL_SIZE;
    const floorGeo = new THREE.PlaneGeometry(mazeSize, mazeSize);
    const floorMat = new THREE.MeshStandardMaterial({
      color: '#4caf50',
      roughness: 0.5,
      metalness: 0.08,
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(mazeSize / 2, 0, mazeSize / 2);
    floor.receiveShadow = true;
    this.scene.add(floor);

    // 玩家出生点：左下角入口
    const startX = CELL_SIZE / 2;
    const startZ = mazeSize - CELL_SIZE * 0.3;
    this.player.setPosition(startX, PLAYER_HEIGHT, startZ);
    this.player.yaw = 0;
    this.player.pitch = 0;

    // 出口：右上角
    const exitX = mazeSize - CELL_SIZE / 2;
    const exitZ = CELL_SIZE / 2;
    this.exit = new Exit(exitX, exitZ);
    this.exit.group.position.set(exitX, 0, exitZ);
    this.scene.add(this.exit.group);
    this.exitLight.position.set(exitX, 1.5, exitZ);
    this.exitLight.intensity = EXIT_LIGHT_INTENSITY;

    // 宝石
    this.gems = [];
    const gemPositions = this.placeGems(config);
    for (const [gx, gz] of gemPositions) {
      const cellCenter = this.mazeData.cellToWorld(
        Math.floor(gz / CELL_SIZE),
        Math.floor(gx / CELL_SIZE),
      );
      const gem = new Gem(cellCenter.x, 0.7, cellCenter.z);
      this.gems.push(gem);
      this.scene.add(gem.group);
    }

    // 天空背景 - 白天
    this.scene.background = new THREE.Color(SKY_COLOR);
    this.scene.fog = new THREE.Fog(SKY_COLOR, mazeSize * FOG_NEAR_RATIO, mazeSize * FOG_FAR_RATIO);

    // 更新相机
    this.camera.position.copy(this.player.position);

    resizeRenderer(this.renderer, this.camera, 2);
  }

  private cleanupMaze(): void {
    if (this.mazeGroup) {
      this.scene.remove(this.mazeGroup);
      this.mazeGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
          if (obj.material instanceof THREE.Material) obj.material.dispose();
        }
      });
      this.mazeGroup = null;
    }
    for (const gem of this.gems) {
      this.scene.remove(gem.group);
      gem.dispose();
    }
    this.gems = [];
    if (this.exit) {
      this.scene.remove(this.exit.group);
      this.exit.dispose();
      this.exit = null;
    }
    // 简单清理：移除除了灯光和必要对象之外的所有对象
    const children = [...this.scene.children];
    for (const child of children) {
      if (
        child !== this.ambientLight &&
        child !== this.flashlight &&
        child !== this.exitLight
      ) {
        this.scene.remove(child);
        if (child instanceof THREE.Group || child instanceof THREE.Mesh) {
          child.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry?.dispose();
              if (Array.isArray(obj.material)) {
                obj.material.forEach((m) => m.dispose());
              } else if (obj.material instanceof THREE.Material) {
                obj.material.dispose();
              }
            }
          });
        }
      }
    }
  }

  private placeGems(config: LevelConfig): [number, number][] {
    if (!this.mazeData) return [];

    const positions: [number, number][] = [];
    const { gridSize, cellToWorld } = this.mazeData;
    const used = new Set<string>();

    // 从除起点和终点外的死路中选位置
    const deadEnds: [number, number][] = [];
    const { walls } = this.mazeData;

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        // 排除起点和终点
        if ((row === gridSize - 1 && col === 0) || (row === 0 && col === gridSize - 1)) continue;

        // 死路判断：3面有墙
        let wallCount = 0;
        for (let d = 0; d < 4; d++) {
          if (walls[row][col][d]) wallCount++;
        }
        if (wallCount === 3) {
          deadEnds.push([row, col]);
        }
      }
    }

    // 如果死路不够，补充一些普通路径
    const allCells: [number, number][] = [];
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        if ((row === gridSize - 1 && col === 0) || (row === 0 && col === gridSize - 1)) continue;
        allCells.push([row, col]);
      }
    }

    // 先放死路
    const shuffledDead = this.shuffleArr([...deadEnds]);
    for (let i = 0; i < Math.min(config.gemCount, shuffledDead.length); i++) {
      const [row, col] = shuffledDead[i];
      const key = `${row},${col}`;
      if (used.has(key)) continue;
      used.add(key);
      const pos = cellToWorld(row, col);
      positions.push([pos.x, pos.z]);
    }

    // 不够就补充
    if (positions.length < config.gemCount) {
      const remaining = this.shuffleArr(
        allCells.filter(([r, c]) => !used.has(`${r},${c}`)),
      );
      for (let i = 0; i < remaining.length && positions.length < config.gemCount; i++) {
        const [row, col] = remaining[i];
        const pos = cellToWorld(row, col);
        positions.push([pos.x, pos.z]);
      }
    }

    return positions;
  }

  private shuffleArr<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // ---- 主循环 ----
  private update(delta: number, _elapsed: number): void {
    this.frameCount++;

    if (this.state === 'start' || this.state === 'paused') {
      this.publishDiagnostics();
      return;
    }

    if (this.state === 'playing') {
      // 更新倒计时
      this.timeLeft -= delta;
      this.hud.updateTimer(Math.max(0, this.timeLeft));

      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.state = 'lost';
        document.exitPointerLock();
        this.hud.showEndScreen(false, this.score, () => this.startLevel(this.currentLevel));
        return;
      }

      // 玩家更新
      this.player.update(delta, this.keys, this.wallColliders, this.camera);

      // 更新相机位置（第一人称）
      this.camera.position.copy(this.player.position);
      const euler = new THREE.Euler(this.player.pitch, this.player.yaw, 0, 'YXZ');
      this.camera.quaternion.setFromEuler(euler);

      // 手电筒跟随
      this.flashlight.position.copy(this.player.position);

      // 宝石更新与碰撞
      for (const gem of this.gems) {
        gem.update(delta, _elapsed);
        if (gem.active) {
          const dx = this.player.position.x - gem.worldPos.x;
          const dz = this.player.position.z - gem.worldPos.z;
          if (dx * dx + dz * dz < GEM_COLLECT_RADIUS * GEM_COLLECT_RADIUS) {
            gem.collect();
            this.gemsCollected++;
            this.score += 100;
            this.hud.updateGems(this.gemsCollected, this.totalGems);
          }
        }
      }

      // 出口检测
      if (this.exit) {
        const dx = this.player.position.x - this.exit.position.x;
        const dz = this.player.position.z - this.exit.position.z;
        if (dx * dx + dz * dz < EXIT_TRIGGER_RADIUS * EXIT_TRIGGER_RADIUS) {
          this.score += Math.floor(this.timeLeft) * 10 + this.gemsCollected * 50;
          this.state = 'won';
          document.exitPointerLock();

          if (this.currentLevel < LEVELS.length - 1) {
            this.hud.showEndScreen(true, this.score, () => this.startLevel(this.currentLevel), () => this.startLevel(this.currentLevel + 1));
          } else {
            // 最后一关
            this.hud.showEndScreen(true, this.score, () => this.startLevel(0));
          }
        }
      }

      // 出口更新
      this.exit?.update(delta, _elapsed);

      // 小地图
      if (this.mazeData) {
        this.hud.updateMinimap(
          this.player.position.x,
          this.player.position.z,
          this.player.yaw,
          this.mazeData.walls,
          this.mazeData.gridSize,
          CELL_SIZE,
          this.visitedCells,
          0,
          this.mazeData.gridSize - 1,
        );
      }
    }

    resizeRenderer(this.renderer, this.camera, 2);
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private publishDiagnostics(): void {
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frameCount,
      state: this.state,
      level: this.currentLevel,
      timeLeft: this.timeLeft,
      score: this.score,
      gemsCollected: this.gemsCollected,
      totalGems: this.totalGems,
      player: {
        x: this.player.position.x,
        y: this.player.position.y,
        z: this.player.position.z,
        yaw: this.player.yaw,
        pitch: this.player.pitch,
      },
      renderer: this.renderer.info.render,
    };
  }
}
